import React, { useState, useRef, useEffect } from 'react';

const FizmasoftTeam = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const mapRef = useRef(null);

  // Refs for sections
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const teamRef = useRef(null);
  const servicesRef = useRef(null);
  const contactRef = useRef(null);

  // Track mouse position for cursor effects
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Enable map scrolling with CTRL key and two-finger touch
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Control' && mapRef.current) {
        mapRef.current.classList.add('map-active');
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control' && mapRef.current) {
        mapRef.current.classList.remove('map-active');
      }
    };

    const handleBlur = () => {
      if (mapRef.current) {
        mapRef.current.classList.remove('map-active');
      }
    };

    const handleTouchStart = (e) => {
      if (mapRef.current && e.touches.length === 2) {
        mapRef.current.classList.add('map-active');
      }
    };

    const handleTouchEnd = (e) => {
      if (mapRef.current && e.touches.length < 2) {
        mapRef.current.classList.remove('map-active');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    if (mapRef.current) {
      mapRef.current.addEventListener('touchstart', handleTouchStart);
      mapRef.current.addEventListener('touchend', handleTouchEnd);
      mapRef.current.addEventListener('touchcancel', handleTouchEnd);
    }

    const currentMapRef = mapRef.current;

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);

      if (currentMapRef) {
        currentMapRef.removeEventListener('touchstart', handleTouchStart);
        currentMapRef.removeEventListener('touchend', handleTouchEnd);
        currentMapRef.removeEventListener('touchcancel', handleTouchEnd);
      }
    };
  }, []);

  // Smooth scroll function
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setIsMenuOpen(false);
  };

  const team = [
    { name: "Mironshoh Asadov", role: "Team Lead Backend", color: "#6366F1" },
    { name: "Behzod Hamroyev", role: "Frontend Developer", color: "#00D9FF" },
    { name: "Ilhomjon Ahadov", role: "Backend Engineer", color: "#FF6B6B" },
    { name: "Umar Akbarov", role: "Software Engineer", color: "#4ECDC4" },
    { name: "Akbarkhuja Khaydarkhujaev", role: "Frontend Developer", color: "#FFE66D" },
    { name: "Muxammadbobur Komiljonov", role: "Frontend Developer", color: "#95E1D3" },
    { name: "Ahilbek Rahimjonov", role: "Software Programmer", color: "#F38181" },
    { name: "Mehriddin Bozorov", role: ".NET Developer", color: "#AA96DA" },
    { name: "Ibrohim Fayzullayev", role: "Developer", color: "#FCBAD3" },
    { name: "Bunyodbek Jurayev", role: "Developer", color: "#A8D8EA" },
    { name: "Diyorbek Ortiqov", role: "Developer", color: "#78DEC7" },
    { name: "Ayyubxon Rustamov", role: "Development Intern", color: "#F9ED69" }
  ];

  const services = [
    { title: "Web Development", desc: "Modern, responsive web applications built with React, Node.js and cutting-edge technologies", icon: "🌐" },
    { title: "Mobile Apps", desc: "Native iOS & Android solutions for seamless mobile experiences", icon: "📲" },
    { title: "Backend Systems", desc: "Scalable server architectures and robust API development", icon: "🗄️" },
    { title: "UI/UX Design", desc: "Beautiful, intuitive user interfaces that delight users", icon: "🎯" },
  ];


  // Generate avatar URL using DiceBear API
  const getAvatarUrl = (name, color) => {
    const seed = name.replace(/\s/g, '');
    return `https://api.dicebear.com/7.x/initials/svg?seed=${seed}&backgroundColor=${color.replace('#', '')}&textColor=ffffff&fontSize=40`;
  };

  // Floating orbs component
  const FloatingOrbs = () => (
    <div className="floating-orbs">
      {[...Array(6)].map((_, i) => (
        <div key={i} className={`orb orb-${i + 1}`} />
      ))}
    </div>
  );

  // Grid pattern component
  const GridPattern = () => (
    <div className="grid-pattern">
      <div className="grid-lines" />
    </div>
  );

  return (
    <div className="fizmasoft-container">
      {/* Cursor Glow Effect */}
      <div
        className="cursor-glow"
        style={{
          left: `${mousePos.x}px`,
          top: `${mousePos.y}px`
        }}
      />

      {/* Background Effects */}
      <FloatingOrbs />
      <GridPattern />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .fizmasoft-container {
          --bg-primary: #0a0a0f;
          --bg-secondary: #12121a;
          --bg-tertiary: #1a1a25;
          --text-primary: #ffffff;
          --text-secondary: #a0a0b0;
          --accent-cyan: #00D9FF;
          --accent-pink: #FF6B6B;
          --accent-green: #4ECDC4;

          font-family: 'Outfit', sans-serif;
          background: var(--bg-primary);
          color: var(--text-primary);
          min-height: 100vh;
          overflow-x: hidden;
          position: relative;
        }

        /* Cursor Glow Effect */
        .cursor-glow {
          position: fixed;
          width: 400px;
          height: 400px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0, 217, 255, 0.15) 0%, rgba(0, 217, 255, 0.05) 30%, transparent 70%);
          pointer-events: none;
          z-index: 2;
          transform: translate(-50%, -50%);
          transition: opacity 0.3s;
          mix-blend-mode: screen;
        }

        /* Floating Orbs Background */
        .floating-orbs {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 0;
          overflow: hidden;
        }

        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.15;
          animation: float 20s infinite ease-in-out;
        }

        .orb-1 {
          width: 400px;
          height: 400px;
          background: var(--accent-cyan);
          top: 10%;
          left: 10%;
          animation-delay: 0s;
        }

        .orb-2 {
          width: 300px;
          height: 300px;
          background: var(--accent-pink);
          top: 60%;
          right: 10%;
          animation-delay: 3s;
        }

        .orb-3 {
          width: 350px;
          height: 350px;
          background: #6366F1;
          bottom: 10%;
          left: 20%;
          animation-delay: 6s;
        }

        .orb-4 {
          width: 250px;
          height: 250px;
          background: var(--accent-green);
          top: 30%;
          right: 30%;
          animation-delay: 9s;
        }

        .orb-5 {
          width: 200px;
          height: 200px;
          background: #FFE66D;
          bottom: 30%;
          right: 20%;
          animation-delay: 12s;
        }

        .orb-6 {
          width: 300px;
          height: 300px;
          background: #AA96DA;
          top: 50%;
          left: 40%;
          animation-delay: 15s;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          25% {
            transform: translate(50px, -50px) scale(1.1);
          }
          50% {
            transform: translate(-30px, 30px) scale(0.9);
          }
          75% {
            transform: translate(40px, 20px) scale(1.05);
          }
        }

        /* Grid Pattern */
        .grid-pattern {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
          overflow: hidden;
        }

        .grid-lines {
          width: 100%;
          height: 100%;
          background-image:
            linear-gradient(rgba(0, 217, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 217, 255, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: gridMove 20s linear infinite;
        }

        @keyframes gridMove {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(50px, 50px);
          }
        }

        /* Navigation */
        .nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          padding: 20px 60px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          backdrop-filter: blur(20px);
          background: rgba(10, 10, 15, 0.8);
          border-bottom: 1px solid rgba(255,255,255,0.05);
          animation: slideDown 0.8s ease-out;
        }

        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .logo {
          font-family: 'Space Mono', monospace;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -1px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: transform 0.3s;
        }

        .logo:hover {
          transform: scale(1.05);
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          overflow: visible;
          animation: logoGlow 3s ease-in-out infinite;
        }

        .logo-icon img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        @keyframes logoGlow {
          0%, 100% {
            filter: drop-shadow(0 0 12px rgba(59, 130, 246, 0.6))
                    drop-shadow(0 0 24px rgba(59, 130, 246, 0.3))
                    drop-shadow(0 0 36px rgba(59, 130, 246, 0.1));
          }
          50% {
            filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.8))
                    drop-shadow(0 0 36px rgba(59, 130, 246, 0.5))
                    drop-shadow(0 0 48px rgba(59, 130, 246, 0.2));
          }
        }

        .nav-links {
          display: flex;
          gap: 40px;
        }

        .nav-link {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary);
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 2px;
          position: relative;
          transition: color 0.3s;
          cursor: pointer;
          background: none;
          border: none;
          font-family: inherit;
        }

        .nav-link:hover {
          color: var(--accent-cyan);
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--accent-cyan);
          transition: width 0.3s;
        }

        .nav-link:hover::after {
          width: 100%;
        }

        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 5px;
        }

        .mobile-menu-btn span {
          width: 25px;
          height: 2px;
          background: var(--text-primary);
          transition: all 0.3s;
        }

        .mobile-menu-btn.active span:nth-child(1) {
          transform: rotate(45deg) translate(5px, 5px);
        }

        .mobile-menu-btn.active span:nth-child(2) {
          opacity: 0;
        }

        .mobile-menu-btn.active span:nth-child(3) {
          transform: rotate(-45deg) translate(5px, -5px);
        }

        .mobile-nav {
          display: none;
          position: fixed;
          top: 80px;
          left: 0;
          right: 0;
          background: rgba(10, 10, 15, 0.98);
          padding: 30px;
          flex-direction: column;
          gap: 20px;
          z-index: 99;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }

        .mobile-nav.open {
          display: flex;
        }

        /* Hero Section */
        .hero {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          text-align: center;
          padding: 120px 60px;
          position: relative;
          z-index: 10;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: rgba(0, 217, 255, 0.1);
          border: 1px solid rgba(0, 217, 255, 0.3);
          border-radius: 50px;
          font-size: 13px;
          font-weight: 500;
          color: var(--accent-cyan);
          margin-bottom: 30px;
          animation: fadeInUp 0.8s ease-out 0.2s both;
        }

        .hero-badge-dot {
          width: 8px;
          height: 8px;
          background: var(--accent-cyan);
          border-radius: 50%;
          animation: blink 1.5s ease-in-out infinite;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        @keyframes fadeInUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .hero-title {
          font-size: clamp(48px, 10vw, 120px);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -4px;
          margin-bottom: 30px;
          animation: fadeInUp 0.8s ease-out 0.4s both;
        }

        .hero-title-line {
          display: block;
        }

        .gradient-text {
          background: linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-green) 50%, var(--accent-pink) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 20px;
          color: var(--text-secondary);
          max-width: 600px;
          line-height: 1.6;
          margin-bottom: 50px;
          animation: fadeInUp 0.8s ease-out 0.6s both;
        }

        .hero-cta {
          display: flex;
          gap: 20px;
          animation: fadeInUp 0.8s ease-out 0.8s both;
        }

        .btn {
          padding: 16px 40px;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s;
          text-decoration: none;
          font-family: inherit;
        }

        .btn-primary {
          background: linear-gradient(135deg, var(--accent-cyan), var(--accent-green));
          color: var(--bg-primary);
          border: none;
        }

        .btn-primary:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 40px rgba(0, 217, 255, 0.3);
        }

        .btn-secondary {
          background: transparent;
          color: var(--text-primary);
          border: 1px solid rgba(255,255,255,0.2);
        }

        .btn-secondary:hover {
          border-color: var(--accent-cyan);
          color: var(--accent-cyan);
        }

        .scroll-indicator {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          animation: fadeInUp 0.8s ease-out 1s both;
          cursor: pointer;
        }

        .scroll-indicator span {
          font-size: 12px;
          color: var(--text-secondary);
          letter-spacing: 2px;
          text-align: center;
          width: 100%;
        }

        .scroll-indicator:hover .scroll-line {
          height: 80px;
        }

        .scroll-line {
          width: 1px;
          height: 60px;
          background: linear-gradient(to bottom, var(--accent-cyan), transparent);
          animation: scrollPulse 2s ease-in-out infinite;
          transition: height 0.3s;
        }

        @keyframes scrollPulse {
          0%, 100% { transform: scaleY(1); opacity: 1; }
          50% { transform: scaleY(0.5); opacity: 0.5; }
        }

        /* CEO Section */
        .ceo-section {
          padding: 120px 60px;
          position: relative;
          z-index: 10;
          background: var(--bg-secondary);
        }

        .ceo-container {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 400px 1fr;
          gap: 80px;
          align-items: center;
        }

        .ceo-image-wrapper {
          position: relative;
        }

        .ceo-image {
          width: 100%;
          aspect-ratio: 1;
          border-radius: 30px;
          object-fit: cover;
          border: 4px solid var(--accent-cyan);
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4);
          position: relative;
          z-index: 2;
          background: var(--bg-tertiary);
        }

        .ceo-image-bg {
          position: absolute;
          top: 20px;
          left: 20px;
          right: -20px;
          bottom: -20px;
          background: linear-gradient(135deg, var(--accent-cyan), var(--accent-green));
          border-radius: 30px;
          opacity: 0.3;
          z-index: 1;
        }

        .ceo-content {
          padding: 20px 0;
        }

        .ceo-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--accent-cyan);
          text-transform: uppercase;
          letter-spacing: 4px;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ceo-label::before {
          content: '';
          width: 40px;
          height: 2px;
          background: var(--accent-cyan);
        }

        .ceo-name {
          font-size: clamp(36px, 5vw, 56px);
          font-weight: 800;
          margin-bottom: 10px;
          letter-spacing: -2px;
        }

        .ceo-title {
          font-size: 20px;
          color: var(--accent-green);
          font-weight: 600;
          margin-bottom: 30px;
        }

        .ceo-bio {
          font-size: 17px;
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 30px;
        }

        .ceo-stats {
          display: flex;
          gap: 40px;
          margin-bottom: 30px;
        }

        .ceo-stat {
          text-align: left;
        }

        .ceo-stat-number {
          font-size: 36px;
          font-weight: 800;
          background: linear-gradient(135deg, var(--accent-cyan), var(--accent-green));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .ceo-stat-label {
          font-size: 13px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .ceo-quote {
          padding: 25px 30px;
          background: rgba(0, 217, 255, 0.05);
          border-left: 4px solid var(--accent-cyan);
          border-radius: 0 16px 16px 0;
          font-style: italic;
          color: var(--text-secondary);
          font-size: 16px;
          line-height: 1.7;
        }

        .ceo-social {
          display: flex;
          gap: 15px;
          margin-top: 30px;
        }

        .ceo-social-link {
          width: 45px;
          height: 45px;
          border-radius: 12px;
          background: var(--bg-tertiary);
          border: 1px solid rgba(255,255,255,0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          text-decoration: none;
          transition: all 0.3s;
        }

        .ceo-social-link:hover {
          background: var(--accent-cyan);
          border-color: var(--accent-cyan);
          transform: translateY(-3px);
        }

        /* Stats Section */
        .stats {
          padding: 100px 60px;
          position: relative;
          z-index: 10;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .stat-card {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 20px;
          padding: 40px;
          text-align: center;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, var(--accent-cyan), transparent);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .stat-card:hover::before {
          opacity: 1;
        }

        .stat-card:hover {
          transform: translateY(-10px);
          background: rgba(255,255,255,0.05);
          border-color: rgba(0, 217, 255, 0.2);
        }

        .stat-number {
          font-size: 56px;
          font-weight: 800;
          background: linear-gradient(135deg, var(--accent-cyan), var(--accent-green));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
        }

        .stat-label {
          font-size: 14px;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        /* Team Section */
        .team {
          padding: 100px 60px;
          position: relative;
          z-index: 10;
        }

        .section-header {
          text-align: center;
          margin-bottom: 80px;
        }

        .section-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--accent-cyan);
          text-transform: uppercase;
          letter-spacing: 4px;
          margin-bottom: 20px;
        }

        .section-title {
          font-size: clamp(36px, 5vw, 64px);
          font-weight: 700;
          letter-spacing: -2px;
        }

        .team-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 30px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .member-card {
          background: var(--bg-secondary);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 24px;
          padding: 40px 30px;
          position: relative;
          overflow: hidden;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
        }

        .member-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, var(--card-color) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.5s;
        }

        .member-card:hover::before {
          opacity: 0.1;
        }

        .member-card:hover {
          transform: translateY(-15px) scale(1.02);
          border-color: var(--card-color);
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.3);
        }

        .member-avatar {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          margin-bottom: 24px;
          position: relative;
          z-index: 1;
          border: 3px solid var(--card-color);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          transition: all 0.5s;
          object-fit: cover;
          background: var(--bg-tertiary);
        }

        .member-card:hover .member-avatar {
          transform: scale(1.1);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
        }

        .member-name {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 8px;
          position: relative;
          z-index: 1;
        }

        .member-role {
          font-size: 14px;
          color: var(--card-color);
          position: relative;
          z-index: 1;
          font-weight: 500;
        }

        .member-accent {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 150px;
          height: 150px;
          background: var(--card-color);
          opacity: 0.05;
          border-radius: 50%;
          transform: translate(30%, 30%);
          transition: all 0.5s;
        }

        .member-card:hover .member-accent {
          transform: translate(20%, 20%) scale(1.5);
          opacity: 0.1;
        }

        /* Services Section */
        .services {
          padding: 100px 60px;
          background: var(--bg-secondary);
          position: relative;
          z-index: 10;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .service-card {
          background: var(--bg-tertiary);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 24px;
          padding: 50px 30px;
          text-align: center;
          transition: all 0.4s;
          position: relative;
          overflow: hidden;
        }

        .service-card:hover {
          transform: translateY(-10px);
          border-color: rgba(0, 217, 255, 0.3);
        }

        .service-icon {
          font-size: 48px;
          margin-bottom: 24px;
          display: block;
        }

        .service-title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 12px;
        }

        .service-desc {
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        /* Contact Section */
        .contact {
          padding: 120px 60px;
          text-align: center;
          position: relative;
          z-index: 10;
        }

        .contact-title {
          font-size: clamp(36px, 6vw, 72px);
          font-weight: 800;
          letter-spacing: -2px;
          margin-bottom: 20px;
        }

        .contact-subtitle {
          font-size: 18px;
          color: var(--text-secondary);
          margin-bottom: 60px;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          max-width: 900px;
          margin: 0 auto 60px;
        }

        .contact-card {
          background: var(--bg-secondary);
          border: 1px solid rgba(255,255,255,0.05);
          border-radius: 20px;
          padding: 30px 20px;
          text-align: center;
          transition: all 0.4s;
        }

        .contact-card:hover {
          transform: translateY(-10px);
          border-color: rgba(0, 217, 255, 0.2);
          background: var(--bg-tertiary);
        }

        .contact-icon {
          font-size: 32px;
          margin-bottom: 15px;
          display: block;
        }

        .contact-card-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
          color: var(--text-primary);
        }

        .contact-card-info {
          font-size: 13px;
          color: var(--text-secondary);
          line-height: 1.6;
        }

        .contact-card-info a {
          color: var(--accent-cyan);
          text-decoration: none;
          transition: color 0.3s;
        }

        .contact-card-info a:hover {
          color: var(--accent-green);
        }

        /* Map Section */
        .map-section {
          padding: 0 60px 120px;
          position: relative;
          z-index: 10;
        }

        .map-wrapper {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
        }

        .map-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 30px;
        }

        .map-title {
          font-size: 28px;
          font-weight: 700;
        }

        .map-address {
          font-size: 14px;
          color: var(--text-secondary);
          text-align: right;
        }

        .map-container {
          width: 100%;
          height: 450px;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,0.1);
          position: relative;
          background: var(--bg-secondary);
        }

        .map-container:hover {
          border-color: rgba(0, 217, 255, 0.2);
        }

        .map-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.02);
          z-index: 5;
          pointer-events: all;
        }

        .map-container.map-active::before {
          pointer-events: none;
          background: transparent;
        }

        .map-container.map-active .map-hint {
          background: rgba(0, 217, 255, 0.2);
          border-color: rgba(0, 217, 255, 0.3);
          color: var(--accent-cyan);
        }

        .map-container iframe {
          width: 100%;
          height: 100%;
          border: none;
          filter: invert(90%) hue-rotate(180deg) brightness(95%) contrast(90%);
        }

        .map-hint {
          position: absolute;
          top: 15px;
          right: 15px;
          background: rgba(0, 0, 0, 0.8);
          padding: 8px 15px;
          border-radius: 8px;
          font-size: 11px;
          color: var(--text-secondary);
          pointer-events: none;
          z-index: 10;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.1);
        }

        .map-hint-mobile {
          display: none;
        }

        @media (max-width: 768px) {
          .map-hint-desktop {
            display: none;
          }

          .map-hint-mobile {
            display: block;
          }
        }

        .map-overlay {
          position: absolute;
          bottom: 20px;
          left: 20px;
          background: var(--bg-primary);
          padding: 20px 25px;
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
        }

        .map-overlay-title {
          font-weight: 700;
          margin-bottom: 5px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .map-overlay-address {
          font-size: 13px;
          color: var(--text-secondary);
        }

        /* Footer */
        .footer {
          padding: 40px 60px;
          border-top: 1px solid rgba(255,255,255,0.05);
          display: flex;
          justify-content: space-between;
          align-items: center;
          position: relative;
          z-index: 10;
        }

        .footer-text {
          font-size: 14px;
          color: var(--text-secondary);
        }

        .footer-links {
          display: flex;
          gap: 30px;
        }

        .footer-link {
          font-size: 14px;
          color: var(--text-secondary);
          text-decoration: none;
          transition: color 0.3s;
          cursor: pointer;
        }

        .footer-link:hover {
          color: var(--accent-cyan);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .ceo-container { grid-template-columns: 1fr; gap: 40px; }
          .ceo-image-wrapper { max-width: 350px; margin: 0 auto; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .services-grid { grid-template-columns: repeat(2, 1fr); }
          .contact-grid { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .nav { padding: 15px 30px; }
          .nav-links { display: none; }
          .mobile-menu-btn { display: flex; }
          .hero { padding: 120px 30px; }
          .hero-cta { flex-direction: column; align-items: center; }
          .scroll-indicator {
            left: 50%;
            right: auto;
            transform: translateX(-50%);
          }
          .scroll-indicator span {
            display: block;
            text-align: center;
          }
          .ceo-section { padding: 80px 30px; }
          .ceo-stats { flex-direction: column; gap: 20px; }
          .stats { padding: 60px 30px; }
          .stats-grid { grid-template-columns: 1fr; }
          .services { padding: 60px 30px; }
          .services-grid { grid-template-columns: 1fr; }
          .team { padding: 60px 30px; }
          .contact { padding: 80px 30px; }
          .map-section { padding: 0 30px 80px; }
          .map-container { height: 350px; }
          .map-header { flex-direction: column; align-items: flex-start; gap: 10px; }
          .map-address { text-align: left; }
          .footer {
            flex-direction: column;
            gap: 20px;
            text-align: center;
            padding: 30px;
          }
        }
      `}</style>


      {/* Navigation */}
      <nav className="nav">
        <div className="logo" onClick={() => scrollToSection(heroRef)}>
          <div className="logo-icon">
            <img src="/logo.svg" alt="Fizmasoft Logo" />
          </div>
          Fizmasoft
        </div>
        <div className="nav-links">
          <button className="nav-link" onClick={() => scrollToSection(heroRef)}>Home</button>
          <button className="nav-link" onClick={() => scrollToSection(aboutRef)}>About</button>
          <button className="nav-link" onClick={() => scrollToSection(teamRef)}>Team</button>
          <button className="nav-link" onClick={() => scrollToSection(servicesRef)}>Services</button>
          <button className="nav-link" onClick={() => scrollToSection(contactRef)}>Contact</button>
        </div>
        <button 
          className={`mobile-menu-btn ${isMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>

      {/* Mobile Navigation */}
      <div className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}>
        <button className="nav-link" onClick={() => scrollToSection(heroRef)}>Home</button>
        <button className="nav-link" onClick={() => scrollToSection(aboutRef)}>About</button>
        <button className="nav-link" onClick={() => scrollToSection(teamRef)}>Team</button>
        <button className="nav-link" onClick={() => scrollToSection(servicesRef)}>Services</button>
        <button className="nav-link" onClick={() => scrollToSection(contactRef)}>Contact</button>
      </div>

      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <div className="hero-badge">
          <span className="hero-badge-dot"></span>
          Building the Future Since 2013
        </div>
        <h1 className="hero-title">
          <span className="hero-title-line">We Are</span>
          <span className="hero-title-line gradient-text">Fizmasoft</span>
        </h1>
        <p className="hero-subtitle">
          A passionate team of software engineers crafting exceptional digital experiences from Tashkent, Uzbekistan to the world.
        </p>
        <div className="hero-cta">
          <button className="btn btn-primary" onClick={() => scrollToSection(teamRef)}>
            Meet Our Team
          </button>
          <button className="btn btn-secondary" onClick={() => scrollToSection(servicesRef)}>
            View Services
          </button>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection(aboutRef)}>
          <span>SCROLL</span>
          <div className="scroll-line"></div>
        </div>
      </section>

      {/* CEO Section */}
      <section className="ceo-section" ref={aboutRef}>
        <div className="ceo-container">
          <div className="ceo-image-wrapper">
            <img 
              src="https://api.dicebear.com/7.x/initials/svg?seed=AzamjonRakhimjonov&backgroundColor=00D9FF&textColor=ffffff&fontSize=36"
              alt="Azamjon Rakhimjonov"
              className="ceo-image"
            />
            <div className="ceo-image-bg"></div>
          </div>
          <div className="ceo-content">
            <div className="ceo-label">Founder & CEO</div>
            <h2 className="ceo-name">Azamjon Rakhimjonov</h2>
            <p className="ceo-title">Chief Executive Officer</p>
            <p className="ceo-bio">
              Azamjon Rakhimjonov is the visionary founder and CEO of Fizmasoft, leading the company since its establishment in 2013. With a strong background in software development and a passion for innovation, he has transformed Fizmasoft into one of Uzbekistan's leading software development companies.
            </p>
            <p className="ceo-bio">
              Before founding Fizmasoft, Azamjon honed his technical expertise as a Software Developer at ABL-Soft&SS. His hands-on experience in development combined with strategic leadership skills has enabled him to build and guide high-performing teams that deliver exceptional digital solutions.
            </p>
            <div className="ceo-stats">
              <div className="ceo-stat">
                <div className="ceo-stat-number">12+</div>
                <div className="ceo-stat-label">Years Leading</div>
              </div>
              <div className="ceo-stat">
                <div className="ceo-stat-number">40+</div>
                <div className="ceo-stat-label">Team Members</div>
              </div>
              <div className="ceo-stat">
                <div className="ceo-stat-number">100+</div>
                <div className="ceo-stat-label">Projects Delivered</div>
              </div>
            </div>
            <div className="ceo-quote">
              "Ideas are implemented into reality, with us. At Fizmasoft, we don't just write code — we build digital experiences that transform businesses and empower people."
            </div>
            <div className="ceo-social">
              <a href="https://linkedin.com/in/arakhimjonov" target="_blank" rel="noopener noreferrer" className="ceo-social-link">💼</a>
              <a href="mailto:a.rakhimjonov@fizmasoft.uz" className="ceo-social-link">✉️</a>
              <a href="https://t.me/a_rakhimjonov" target="_blank" rel="noopener noreferrer" className="ceo-social-link">📱</a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats">
        <div className="stats-grid">
          {[
            { number: "12+", label: "Years Experience" },
            { number: "100+", label: "Projects Delivered" },
            { number: "40+", label: "Team Members" },
            { number: "100%", label: "Client Satisfaction" },
          ].map((stat, i) => (
            <div 
              key={i} 
              className="stat-card"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Team Section */}
      <section className="team" ref={teamRef}>
        <div className="section-header">
          <div className="section-label">Our Team</div>
          <h2 className="section-title">Meet the Innovators</h2>
        </div>
        <div className="team-grid">
          {team.map((member, i) => (
            <div
              key={i}
              className="member-card"
              style={{ '--card-color': member.color }}
              onMouseEnter={() => setHoveredMember(i)}
              onMouseLeave={() => setHoveredMember(null)}
            >
              <img 
                src={getAvatarUrl(member.name, member.color)}
                alt={member.name}
                className="member-avatar"
              />
              <h3 className="member-name">{member.name}</h3>
              <p className="member-role">{member.role}</p>
              <div className="member-accent"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="services" ref={servicesRef}>
        <div className="section-header">
          <div className="section-label">What We Do</div>
          <h2 className="section-title">Our Expertise</h2>
        </div>
        <div className="services-grid">
          {services.map((service, i) => (
            <div key={i} className="service-card">
              <span className="service-icon">{service.icon}</span>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-desc">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" ref={contactRef}>
        <h2 className="contact-title">Let's Build Something <span className="gradient-text">Amazing</span></h2>
        <p className="contact-subtitle">Ready to start your next project? Get in touch with us.</p>
        
        <div className="contact-grid">
          <div className="contact-card">
            <span className="contact-icon">📍</span>
            <h3 className="contact-card-title">Our Office</h3>
            <p className="contact-card-info">
              Malaya kolsevaya doroga, 2<br />
              Almazarsky District<br />
              Tashkent, Uzbekistan
            </p>
          </div>
          <div className="contact-card">
            <span className="contact-icon">📞</span>
            <h3 className="contact-card-title">Phone</h3>
            <p className="contact-card-info">
              <a href="tel:+998555020404">+998 55 502 04 04 (ext. 121)</a>
            </p>
          </div>
          <div className="contact-card">
            <span className="contact-icon">✉️</span>
            <h3 className="contact-card-title">Email</h3>
            <p className="contact-card-info">
              <a href="mailto:info@fizmasoft.uz">info@fizmasoft.uz</a>
            </p>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="map-wrapper">
          <div className="map-header">
            <h3 className="map-title">📍 Find Us Here</h3>
            <p className="map-address">
              Malaya kolsevaya doroga, 2, Almazarsky District<br />
              Tashkent, Uzbekistan
            </p>
          </div>
          <div className="map-container" ref={mapRef}>
            <div className="map-hint map-hint-desktop">Hold CTRL to zoom/move map</div>
            <div className="map-hint map-hint-mobile">Use two fingers to zoom/move map</div>
            <iframe
              src="https://www.openstreetmap.org/export/embed.html?bbox=69.2098%2C41.3407%2C69.2298%2C41.3607&layer=mapnik&marker=41.350679%2C69.219818"
              title="Fizmasoft Office Location - Tashkent, Uzbekistan"
              loading="lazy"
            />
            <div className="map-overlay">
              <div className="map-overlay-title">
                <span>🏢</span> Fizmasoft HQ
              </div>
              <div className="map-overlay-address">
                Malaya kolsevaya doroga, 2, Almazarsky District
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-text">© 2024 Fizmasoft. All rights reserved.</div>
        <div className="footer-links">
          <a href="https://linkedin.com/company/fizmasoft" target="_blank" rel="noopener noreferrer" className="footer-link">LinkedIn</a>
          <a href="https://github.com/fizmasoft" target="_blank" rel="noopener noreferrer" className="footer-link">GitHub</a>
          <a href="https://t.me/fizmasoft_team" target="_blank" rel="noopener noreferrer" className="footer-link">Telegram</a>
        </div>
      </footer>
    </div>
  );
};

export default FizmasoftTeam;